"use client";

import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import appConfig from "@/config";

/* ─────────────────────────── types ─────────────────────────── */

interface MathpixData {
  text: string | null;
  latex: string | null;
  latex_styled: string | null;
  confidence: number | null;
  word_data: unknown;
  line_data_raw: string[] | null;
  line_data: string[] | null;
  canvas_image: string | null;
  ocr_status: string;
  raw_version: number;
  cleanup_version: number;
  timestamp: number | null;
}

interface FeedbackRequest {
  type: string;
  subvraag_id: number;
  subvraag_letter: string;
  vraag_tekst: string;
  worked_solution: string;
  student_work_latex: string;
  fase: number;
  conversation_history_count: number;
  message_chain: { role: string; content: string }[];
  prompt: string;
}

interface FeedbackResponse {
  feedback: string;
  model: string;
  duration_ms: number;
  raw_response: Record<string, unknown>;
  status: string;
  result: string;
  raw_result: string;
  error: string | null;
}

interface FeedbackEntry {
  request: FeedbackRequest;
  response: FeedbackResponse;
  timestamp: number | null;
}

/* ─────────────────────────── helpers ─────────────────────────── */

/** Confidence → hsl color string */
function confidenceColor(c: number | null): string {
  if (c === null || c === undefined) return "hsl(0, 0%, 40%)";
  // 0 → red (0°), 1 → green (120°)
  const hue = Math.round(c * 120);
  return `hsl(${hue}, 80%, 45%)`;
}

/** Format unix timestamp to readable string */
function formatTimestamp(ts: number | null): string {
  if (!ts) return "—";
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
}

/** Compute simple word-level diffs between raw and cleaned arrays */
function computeDiffs(
  raw: string[] | null,
  cleaned: string[] | null
): { raw: string; cleaned: string; changed: boolean }[] {
  if (!raw && !cleaned) return [];
  const r = raw ?? [];
  const c = cleaned ?? [];
  const maxLen = Math.max(r.length, c.length);
  const result: { raw: string; cleaned: string; changed: boolean }[] = [];
  const toStr = (v: any): string =>
    typeof v === "string" ? v : (v?.text ?? "");
  for (let i = 0; i < maxLen; i++) {
    const rawLine = toStr(r[i]);
    const cleanLine = toStr(c[i]);
    result.push({
      raw: rawLine,
      cleaned: cleanLine,
      changed: rawLine !== cleanLine
    });
  }
  return result;
}

/** Status badge color */
function statusColor(status: string): string {
  switch (status) {
    case "correct":
      return "bg-green-600";
    case "on_track":
      return "bg-blue-600";
    case "minor_error":
      return "bg-yellow-600";
    case "incorrect":
      return "bg-red-600";
    default:
      return "bg-gray-600";
  }
}

/** Feedback type badge color */
function typeBadgeColor(type: string): string {
  return type === "check" ? "bg-purple-600" : "bg-indigo-600";
}

/* ─────────────────────────── components ─────────────────────────── */

/** Rendered markdown + math block */
function RenderedContent({ content }: { content: string }) {
  return (
    <div className="prose prose-invert prose-sm max-w-none break-words">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

/** Single message in a conversation chain */
function MessageBubble({
  role,
  content: rawContent
}: {
  role: string;
  content: any;
}) {
  const content: string =
    typeof rawContent === "string"
      ? rawContent
      : (rawContent?.text ?? JSON.stringify(rawContent));
  const [expanded, setExpanded] = useState(role === "assistant");

  const roleColors: Record<string, string> = {
    system: "border-yellow-600 bg-yellow-950/30",
    user: "border-blue-600 bg-blue-950/30",
    assistant: "border-green-600 bg-green-950/30"
  };

  const colorClass = roleColors[role] ?? "border-gray-600 bg-gray-950/30";

  return (
    <div className={`border-l-2 ${colorClass} pl-3 py-1 mb-2`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-xs font-mono text-gray-400 hover:text-gray-200 flex items-center gap-1"
      >
        <span className="text-[10px]">{expanded ? "▼" : "▶"}</span>
        <span className="uppercase font-bold">{role}</span>
        {!expanded && (
          <span className="text-gray-500 ml-2 truncate max-w-[300px]">
            {content.slice(0, 80)}…
          </span>
        )}
      </button>
      {expanded && (
        <div className="mt-1 text-sm">
          <RenderedContent content={content} />
        </div>
      )}
    </div>
  );
}

/** Feedback card — memoized to avoid re-renders on polling */
const FeedbackCard = memo(function FeedbackCard({
  entry,
  defaultExpanded = false
}: {
  entry: FeedbackEntry;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const req = entry.request;
  const res = entry.response;

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-750 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className="text-[10px]">{expanded ? "▼" : "▶"}</span>
          <span
            className={`text-xs px-2 py-0.5 rounded font-mono uppercase ${typeBadgeColor(req.type)}`}
          >
            {req.type}
          </span>
          <span className="text-sm text-gray-300">
            Subvraag {req.subvraag_letter} · Fase {req.fase}
          </span>
          {res?.status && (
            <span
              className={`text-xs px-2 py-0.5 rounded ${statusColor(res.status)}`}
            >
              {res.status}
            </span>
          )}
          {res?.error && (
            <span className="text-xs px-2 py-0.5 rounded bg-red-800">
              ERROR
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {res?.duration_ms > 0 && <span>{res.duration_ms}ms</span>}
          <span>{res?.model}</span>
          <span>{formatTimestamp(entry.timestamp)}</span>
        </div>
      </button>

      {/* Body */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-700 pt-3 space-y-4">
          {/* Request info */}
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <span className="text-gray-500">Vraag:</span>
              <p className="text-gray-300 mt-1">{req.vraag_tekst}</p>
            </div>
            <div>
              <span className="text-gray-500">Student werk (LaTeX):</span>
              <pre className="text-gray-300 mt-1 text-[11px] whitespace-pre-wrap bg-gray-900 rounded p-2 max-h-32 overflow-auto">
                {req.student_work_latex || "—"}
              </pre>
            </div>
          </div>

          {/* Worked solution */}
          <div className="text-xs">
            <span className="text-gray-500">Uitwerking:</span>
            <pre className="text-gray-300 mt-1 text-[11px] whitespace-pre-wrap bg-gray-900 rounded p-2 max-h-40 overflow-auto">
              {req.worked_solution || "—"}
            </pre>
          </div>

          {/* Message chain */}
          {req.message_chain && req.message_chain.length > 0 && (
            <div>
              <span className="text-xs text-gray-500 block mb-2">
                Message Chain ({req.message_chain.length} messages):
              </span>
              <div className="max-h-80 overflow-auto">
                {req.message_chain.map((msg, i) => (
                  <MessageBubble
                    key={i}
                    role={msg.role}
                    content={msg.content}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Response */}
          <div>
            <span className="text-xs text-gray-500 block mb-2">Response:</span>
            <div className="bg-gray-900 rounded p-3">
              <RenderedContent
                content={res?.feedback || res?.result || res?.error || "—"}
              />
            </div>
          </div>

          {/* Raw result if different from parsed */}
          {res?.raw_result && res?.raw_result !== res?.result && (
            <div>
              <span className="text-xs text-gray-500 block mb-1">
                Raw result (unparsed):
              </span>
              <pre className="text-[11px] text-gray-400 whitespace-pre-wrap bg-gray-900 rounded p-2 max-h-32 overflow-auto">
                {res?.raw_result}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

/** OCR status indicator dot */
function StatusDot({ label, active }: { label: string; active: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      <div
        className={`w-2.5 h-2.5 rounded-full ${
          active
            ? "bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.6)]"
            : "bg-gray-600"
        }`}
      />
      <span
        className={`text-xs font-mono ${active ? "text-green-400" : "text-gray-500"}`}
      >
        {label}
      </span>
    </div>
  );
}

/** Confidence color legend bar */
function ConfidenceLegend() {
  const buckets = 10;
  return (
    <div className="flex items-center gap-1">
      <span className="text-[10px] text-gray-500 mr-1">0%</span>
      {Array.from({ length: buckets }).map((_, i) => (
        <div
          key={i}
          className="h-3 flex-1 rounded-sm min-w-[20px]"
          style={{ backgroundColor: confidenceColor(i / (buckets - 1)) }}
        />
      ))}
      <span className="text-[10px] text-gray-500 ml-1">100%</span>
    </div>
  );
}

/* ─────────────────────────── main page ─────────────────────────── */

function MonitorPageInner() {
  const [mathpix, setMathpix] = useState<MathpixData | null>(null);
  const [latestFeedback, setLatestFeedback] = useState<FeedbackEntry | null>(
    null
  );
  const [feedbackHistory, setFeedbackHistory] = useState<FeedbackEntry[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Ref-based dedup for feedback polling
  const lastFeedbackTs = useRef<number | null>(null);
  const lastHistoryLen = useRef<number>(0);

  /* ── Mathpix polling (1s) ── */
  const fetchMathpix = useCallback(async () => {
    try {
      const res = await fetch("/api/development/ipad/mathpix/latest");
      if (!res.ok) throw new Error(`${res.status}`);
      const data: MathpixData = await res.json();
      setMathpix(data);
      setLastUpdate(new Date());
      setConnectionError(null);
    } catch (err) {
      setConnectionError(
        `Mathpix fetch failed: ${err instanceof Error ? err.message : "unknown"}`
      );
    }
  }, []);

  /* ── Feedback polling (2s) ── */
  const fetchFeedback = useCallback(async () => {
    try {
      const [latestRes, historyRes] = await Promise.all([
        fetch("/api/development/ipad/tutor/latest"),
        fetch("/api/development/ipad/tutor/history")
      ]);

      if (latestRes.ok) {
        const data: FeedbackEntry = await latestRes.json();
        // Only update if timestamp changed
        if (data.timestamp !== lastFeedbackTs.current) {
          lastFeedbackTs.current = data.timestamp;
          setLatestFeedback(data);
        }
      }

      if (historyRes.ok) {
        const data: FeedbackEntry[] = await historyRes.json();
        // Only update if length changed
        if (data.length !== lastHistoryLen.current) {
          lastHistoryLen.current = data.length;
          setFeedbackHistory(data);
        }
      }

      setConnectionError(null);
    } catch (err) {
      setConnectionError(
        `Feedback fetch failed: ${err instanceof Error ? err.message : "unknown"}`
      );
    }
  }, []);

  useEffect(() => {
    // Initial fetch
    fetchMathpix();
    fetchFeedback();

    const mathpixInterval = setInterval(fetchMathpix, 1000);
    const feedbackInterval = setInterval(fetchFeedback, 2000);

    return () => {
      clearInterval(mathpixInterval);
      clearInterval(feedbackInterval);
    };
  }, [fetchMathpix, fetchFeedback]);

  /* ── Derived state ── */
  const ocrStatus = mathpix?.ocr_status ?? "idle";
  const confidence = mathpix?.confidence ?? null;
  const rawVersion = mathpix?.raw_version ?? 0;
  const cleanupVersion = mathpix?.cleanup_version ?? 0;
  const versionMismatch = rawVersion !== cleanupVersion;
  const diffs = computeDiffs(
    mathpix?.line_data_raw ?? null,
    mathpix?.line_data ?? null
  );
  const hasData = mathpix && (mathpix.line_data_raw || mathpix.canvas_image);

  // Combine cleaned lines into a single string for MathJax rendering
  const cleanedText = mathpix?.line_data
    ? mathpix.line_data
        .map((line: any) => {
          const s =
            typeof line === "string"
              ? line
              : (line?.text ?? String(line ?? ""));
          // Wrap LaTeX expressions for rendering
          if (s.includes("\\") || s.includes("$")) {
            return s.startsWith("$") ? s : `$${s}$`;
          }
          return s;
        })
        .join("\n\n")
    : "";

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 lg:p-6">
      {/* ── Header ── */}
      <header className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-white">iPad Monitor</h1>
          <div className="flex items-center gap-3">
            <StatusDot label="Mathpix" active={ocrStatus === "mathpix"} />
            <StatusDot label="Cleanup" active={ocrStatus === "cleanup"} />
            <StatusDot label="Idle" active={ocrStatus === "idle"} />
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs font-mono">
          {/* Version counters */}
          <div className="flex items-center gap-2">
            <span className="text-gray-500">raw v{rawVersion}</span>
            <span className="text-gray-500">·</span>
            <span className="text-gray-500">clean v{cleanupVersion}</span>
            {versionMismatch && (
              <span
                className="text-yellow-400 animate-pulse"
                title="Version mismatch — cleanup is behind"
              >
                ⚠ mismatch
              </span>
            )}
          </div>

          {/* Connection error */}
          {connectionError && (
            <span className="text-red-400 text-[11px]">{connectionError}</span>
          )}
        </div>
      </header>

      {!hasData ? (
        /* ── Empty state ── */
        <div className="flex flex-col items-center justify-center h-[60vh] text-gray-500">
          <div className="w-16 h-16 border-2 border-gray-700 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 animate-pulse"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834.166l-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243l-1.59-1.59"
              />
            </svg>
          </div>
          <p className="text-lg">Waiting for input from iPad...</p>
          <p className="text-sm mt-2 text-gray-600">
            Drawing and OCR data will appear here in real time
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* ── Canvas image ── */}
          {mathpix?.canvas_image && (
            <section>
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Canvas Preview
              </h2>
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-3 inline-block">
                <img
                  src={`data:image/jpeg;base64,${mathpix.canvas_image}`}
                  alt="Student handwriting"
                  className="max-h-48 rounded bg-white"
                />
              </div>
            </section>
          )}

          {/* ── 3-column OCR grid ── */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
                OCR Recognition
              </h2>
              {confidence !== null && (
                <span
                  className="text-xs font-mono px-2 py-0.5 rounded"
                  style={{
                    backgroundColor: confidenceColor(confidence),
                    color: confidence > 0.5 ? "#000" : "#fff"
                  }}
                >
                  {(confidence * 100).toFixed(1)}% confidence
                </span>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              {/* Raw Mathpix */}
              <div
                className="bg-gray-800 rounded-lg border-2 p-4"
                style={{
                  borderColor:
                    ocrStatus === "mathpix"
                      ? confidenceColor(confidence)
                      : "rgb(55, 65, 81)"
                }}
              >
                <h3 className="text-xs font-mono text-gray-500 mb-2 uppercase">
                  Raw Mathpix
                </h3>
                <div className="space-y-1 font-mono text-sm">
                  {diffs.length > 0 ? (
                    diffs.map((d, i) => (
                      <div key={i} className="text-gray-300">
                        {d.raw || <span className="text-gray-600">—</span>}
                      </div>
                    ))
                  ) : (
                    <span className="text-gray-600">No data</span>
                  )}
                </div>
              </div>

              {/* GPT-cleaned */}
              <div
                className="bg-gray-800 rounded-lg border-2 p-4"
                style={{
                  borderColor:
                    ocrStatus === "cleanup"
                      ? confidenceColor(confidence)
                      : "rgb(55, 65, 81)"
                }}
              >
                <h3 className="text-xs font-mono text-gray-500 mb-2 uppercase">
                  GPT-5.1 Cleaned
                </h3>
                <div className="space-y-1 font-mono text-sm">
                  {diffs.length > 0 ? (
                    diffs.map((d, i) => (
                      <div
                        key={i}
                        className={
                          d.changed ? "text-green-400" : "text-gray-300"
                        }
                      >
                        {d.cleaned || <span className="text-gray-600">—</span>}
                      </div>
                    ))
                  ) : (
                    <span className="text-gray-600">No data</span>
                  )}
                </div>
              </div>

              {/* Rendered */}
              <div className="bg-gray-800 rounded-lg border-2 border-gray-700 p-4">
                <h3 className="text-xs font-mono text-gray-500 mb-2 uppercase">
                  Rendered
                </h3>
                {cleanedText ? (
                  <RenderedContent content={cleanedText} />
                ) : (
                  <span className="text-gray-600 text-sm">No data</span>
                )}
              </div>
            </div>
          </section>

          {/* ── Confidence legend ── */}
          <section className="max-w-md">
            <h2 className="text-xs font-mono text-gray-500 uppercase mb-1">
              Confidence Scale
            </h2>
            <ConfidenceLegend />
          </section>

          {/* ── Latest Feedback ── */}
          {latestFeedback && (
            <section>
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Latest Feedback
              </h2>
              <FeedbackCard entry={latestFeedback} defaultExpanded />
            </section>
          )}

          {/* ── Feedback History ── */}
          {feedbackHistory.length > 1 && (
            <section>
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Feedback History ({feedbackHistory.length - 1} previous)
              </h2>
              <div className="space-y-2">
                {feedbackHistory.slice(1).map((entry, i) => (
                  <FeedbackCard key={`history-${i}`} entry={entry} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* ── Status footer ── */}
      <footer className="mt-8 pt-4 border-t border-gray-800 flex items-center justify-between text-xs text-gray-600">
        <span>iPad Monitor — Development Dashboard</span>
        <span>
          Last update:{" "}
          {lastUpdate
            ? lastUpdate.toLocaleTimeString("nl-NL", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit"
              })
            : "—"}
        </span>
      </footer>
    </div>
  );
}

export default function MonitorPage() {
  if (appConfig.environment !== "development") {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-gray-500">
        <p>Not available.</p>
      </div>
    );
  }

  return <MonitorPageInner />;
}
